import { isOk, Result } from "@/common/common";
import { TMessage } from "@/i18n/tt";
import { ExerciseReceiver, HolderHoldingCompanies, HoldingCompany } from "./instrumentsApiClient";

export type ReceiverRow = {
  trancheId: string;
  holderId: string;
  holderName: string;
  isSplit: boolean;
  receiver: ExerciseReceiver;
};

export type ReceiverState = {
  rows: ReceiverRow[];
  holdingCompaniesResult?: Result<HolderHoldingCompanies[]>;
};

export const holdingCompaniesOf = (state: ReceiverState, holderId: string): HoldingCompany[] =>
  isOk(state.holdingCompaniesResult)
    ? (state.holdingCompaniesResult.data.find((entry) => entry.holderId === holderId)
        ?.holdingCompanies ?? [])
    : [];

const sameReceiver = (a: ExerciseReceiver, b: ExerciseReceiver): boolean =>
  a.type === "HOLDER"
    ? b.type === "HOLDER"
    : b.type === "HOLDING_COMPANY" && a.organisationNumber === b.organisationNumber;

export type ExerciseReceiverChoice =
  | { type: "SELF" }
  | { type: "SELF_AND_HOLDING" }
  | { type: "COMPANY"; company: HoldingCompany };

export const companyReceiver = (company: HoldingCompany): ExerciseReceiver => ({
  type: "HOLDING_COMPANY",
  organisationNumber: company.organisationNumber,
});

export const isSplitTranche = (state: ReceiverState, trancheId: string): boolean =>
  state.rows.some((row) => row.isSplit && row.trancheId === trancheId);

export const receiverChoices = (
  state: ReceiverState,
  row: ReceiverRow,
): ExerciseReceiverChoice[] => {
  const companies = holdingCompaniesOf(state, row.holderId).map(
    (company): ExerciseReceiverChoice => ({ type: "COMPANY", company }),
  );
  if (row.isSplit) {
    return companies;
  }
  return [
    { type: "SELF" },
    ...(companies.length > 0 ? [{ type: "SELF_AND_HOLDING" } as const] : []),
    ...companies,
  ];
};

export const selectedReceiverChoice = (
  state: ReceiverState,
  row: ReceiverRow,
): ExerciseReceiverChoice | undefined =>
  receiverChoices(state, row).find((choice) => {
    if (!row.isSplit && isSplitTranche(state, row.trancheId)) {
      return choice.type === "SELF_AND_HOLDING";
    }
    switch (choice.type) {
      case "SELF":
        return row.receiver.type === "HOLDER";
      case "SELF_AND_HOLDING":
        return false;
      case "COMPANY":
        return sameReceiver(row.receiver, companyReceiver(choice.company));
    }
  });

export const receiverChoiceId = (choice: ExerciseReceiverChoice): string =>
  choice.type === "COMPANY" ? choice.company.organisationNumber : choice.type;

const ownershipSource = (company: HoldingCompany): TMessage =>
  company.source === "DCOMPANY_REGISTER"
    ? { key: "options.exercise-source-register" }
    : company.asOf === undefined
      ? { key: "options.exercise-source-trank" }
      : {
          key: "options.exercise-source-trank-date",
          params: { date: { type: "date", value: company.asOf } },
        };

export const holdingCompanyDetails = (company: HoldingCompany): TMessage => ({
  key: "options.exercise-holding-company-details",
  params: {
    orgNo: { type: "org-no", value: company.organisationNumber },
    source: ownershipSource(company),
  },
});

export const receiverCompany = (
  state: ReceiverState,
  row: ReceiverRow,
): HoldingCompany | undefined => {
  const { receiver } = row;
  return receiver.type === "HOLDING_COMPANY"
    ? holdingCompaniesOf(state, row.holderId).find(
        (company) => company.organisationNumber === receiver.organisationNumber,
      )
    : undefined;
};

export const exerciseReceiverName = (state: ReceiverState, row: ReceiverRow): string =>
  receiverCompany(state, row)?.name ?? row.holderName;
